import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaPlus, FaCalendar } from 'react-icons/fa';
import axiosPost from '../../../helpers/axios-helper';
import axios from 'axios';
import config from '../../../constants/config';

export default function Events() {
  const EVENT_TYPES = [
    { value: 'lgbtq', label: 'LGBTQ+' },
    { value: 'cultural', label: 'Cultural' },
    { value: 'networking', label: 'Networking' },
    { value: 'workshop', label: 'Workshop' },
    { value: 'party', label: 'Party' },
    { value: 'festival', label: 'Festival' },
    { value: 'other', label: 'Other' }
  ];

  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    country_id: '',
    event_type: 'lgbtq',
    event_date: '',
    end_date: '',
    image_url: '',
    website_url: '',
    ticket_url: '',
    is_featured: 0,
    active: 1
  });

  const [countries, setCountries] = useState([]);
  const [filters, setFilters] = useState({ country_id: '', event_type: '', active: '' });
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // Load events list
  const loadEvents = (controller, overrides = {}) => {
    const payload = { page: 1, per_page: 100 };
    const countryId = overrides.country_id ?? filters.country_id;
    const eventType = overrides.event_type ?? filters.event_type;
    const activeVal = overrides.active ?? filters.active;

    if (countryId) payload.country_id = parseInt(countryId);
    if (eventType) payload.event_type = eventType;
    if (activeVal !== '' && activeVal !== undefined && activeVal !== null) {
      payload.active = parseInt(activeVal);
    }

    axiosPost(
      'events/list',
      { 'Content-Type': 'application/json' },
      payload,
      (resp) => {
        if (resp?.success && resp?.data?.items) {
          setEvents(resp.data.items);
        } else {
          setEvents([]);
        }
        setIsLoading(false);
      },
      controller
    );
  };

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    loadEvents(controller);

    // Fetch countries list
    axiosPost(
      'dashboard/countries/list',
      {},
      {},
      (resp) => {
        const list = Array.isArray(resp) ? resp : (Array.isArray(resp?.data) ? resp.data : []);
        setCountries(list);
      },
      controller
    );

    return () => controller.abort();
  }, []);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    const controller = new AbortController();
    setIsLoading(true);
    loadEvents(controller, newFilters);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  const handleEdit = (event) => {
    setSelectedEvent(event);
    setIsEditing(true);

    // Convert timestamps to datetime-local format
    const eventDate = event.event_date ? new Date(event.event_date * 1000).toISOString().slice(0, 16) : '';
    const endDate = event.end_date ? new Date(event.end_date * 1000).toISOString().slice(0, 16) : '';

    setFormData({
      id: event.id,
      title: event.title || '',
      description: event.description || '',
      location: event.location || '',
      country_id: event.country_id || '',
      event_type: event.event_type || 'lgbtq',
      event_date: eventDate,
      end_date: endDate,
      image_url: event.image_url || '',
      website_url: event.website_url || '',
      ticket_url: event.ticket_url || '',
      is_featured: event.is_featured || 0,
      active: event.active !== undefined ? event.active : 1
    });
  };

  const handleNew = () => {
    setSelectedEvent(null);
    setIsEditing(true);
    setFormData({
      title: '',
      description: '',
      location: '',
      country_id: '',
      event_type: 'lgbtq',
      event_date: '',
      end_date: '',
      image_url: '',
      website_url: '',
      ticket_url: '',
      is_featured: 0,
      active: 1
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSelectedEvent(null);
    setFormData({
      title: '',
      description: '',
      location: '',
      country_id: '',
      event_type: 'lgbtq',
      event_date: '',
      end_date: '',
      image_url: '',
      website_url: '',
      ticket_url: '',
      is_featured: 0,
      active: 1
    });
  };

  const handleSave = () => {
    if (!formData.title || !formData.country_id || !formData.event_date) {
      alert('Please fill in required fields: Title, Country, and Event Date');
      return;
    }

    // Convert datetime-local to UNIX timestamp
    const eventTimestamp = formData.event_date ? Math.floor(new Date(formData.event_date).getTime() / 1000) : null;
    const endTimestamp = formData.end_date ? Math.floor(new Date(formData.end_date).getTime() / 1000) : null;

    const payload = {
      ...formData,
      event_date: eventTimestamp,
      end_date: endTimestamp,
      country_id: parseInt(formData.country_id)
    };

    if (selectedEvent) {
      payload.id = selectedEvent.id;
    }

    axiosPost(
      'events/update',
      { 'Content-Type': 'application/json' },
      payload,
      (resp) => {
        if (resp?.success) {
          alert(selectedEvent ? 'Event updated successfully!' : 'Event created successfully!');
          handleCancel();
          const controller = new AbortController();
          loadEvents(controller);
        } else {
          alert('Error: ' + (resp?.response || 'Failed to save event'));
        }
      }
    );
  };

  const handleDelete = (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    axiosPost(
      'events/delete',
      { 'Content-Type': 'application/json' },
      { id: eventId },
      (resp) => {
        if (resp?.success) {
          alert('Event deleted successfully!');
          const controller = new AbortController();
          loadEvents(controller);
        } else {
          alert('Error: ' + (resp?.response || 'Failed to delete event'));
        }
      }
    );
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingImage(true);
    const formDataUpload = new FormData();
    formDataUpload.append('image', file);

    try {
      const response = await axios.post(
        `${config.api.url}events/upload-image`,
        formDataUpload,
        {
          headers: {
            'api': config.api.key,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data?.success && response.data?.data?.url) {
        const imageUrl = response.data.data.url;
        setFormData(prev => ({ ...prev, image_url: imageUrl }));
        alert('Image uploaded successfully!');
      } else {
        alert('Upload failed: ' + (response.data?.response || 'Unknown error'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed: ' + (error.message || 'Unknown error'));
    } finally {
      setIsUploadingImage(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getCountryName = (countryId) => {
    const country = countries.find(c => c.id === countryId);
    return country ? country.name : '-';
  };

  return (
    <div className="dashboard-module">
      <div className="module-header">
        <h2>Events Management</h2>
        <button className="btn-primary" onClick={handleNew}>
          <FaPlus /> New Event
        </button>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <select
          value={filters.country_id}
          onChange={(e) => handleFilterChange('country_id', e.target.value)}
        >
          <option value="">All Countries</option>
          {countries.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        <select
          value={filters.event_type}
          onChange={(e) => handleFilterChange('event_type', e.target.value)}
        >
          <option value="">All Types</option>
          {EVENT_TYPES.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>

        <select
          value={filters.active}
          onChange={(e) => handleFilterChange('active', e.target.value)}
        >
          <option value="">All Status</option>
          <option value="1">Active</option>
          <option value="0">Inactive</option>
        </select>
      </div>

      {/* Event List */}
      {!isEditing && (
        <div className="items-list">
          {isLoading ? (
            <div className="loading">Loading events...</div>
          ) : events.length === 0 ? (
            <div className="empty-state">No events found. Create your first event!</div>
          ) : (
            <table className="items-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Country</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Location</th>
                  <th>Featured</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map(event => (
                  <tr key={event.id}>
                    <td>{event.title}</td>
                    <td>{getCountryName(event.country_id)}</td>
                    <td>
                      <span className={`badge badge-${event.event_type}`}>
                        {EVENT_TYPES.find(t => t.value === event.event_type)?.label || event.event_type}
                      </span>
                    </td>
                    <td>{formatDate(event.event_date)}</td>
                    <td>{event.location || '-'}</td>
                    <td>{event.is_featured ? '⭐' : '-'}</td>
                    <td>
                      <span className={`status-badge ${event.active ? 'active' : 'inactive'}`}>
                        {event.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <button className="btn-icon" onClick={() => handleEdit(event)}>
                        <FaEdit />
                      </button>
                      <button className="btn-icon btn-danger" onClick={() => handleDelete(event.id)}>
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Event Form */}
      {isEditing && (
        <div className="edit-form">
          <h3>{selectedEvent ? 'Edit Event' : 'New Event'}</h3>

          <div className="form-grid">
            <div className="form-group full-width">
              <label>Title *</label>
              <input
                className="dashboard-input"
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Event title"
              />
            </div>

            <div className="form-group">
              <label>Country *</label>
              <select
                className="dashboard-select"
                name="country_id"
                value={formData.country_id}
                onChange={handleInputChange}
              >
                <option value="">Select Country</option>
                {countries.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Event Type</label>
              <select
                className="dashboard-select"
                name="event_type"
                value={formData.event_type}
                onChange={handleInputChange}
              >
                {EVENT_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Event Date *</label>
              <input
                className="dashboard-input"
                type="datetime-local"
                name="event_date"
                value={formData.event_date}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>End Date (Optional)</label>
              <input
                className="dashboard-input"
                type="datetime-local"
                name="end_date"
                value={formData.end_date}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group full-width">
              <label>Location</label>
              <input
                className="dashboard-input"
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="e.g., Willemstad, Curaçao"
              />
            </div>

            <div className="form-group full-width">
              <label>Description</label>
              <textarea
                className="dashboard-textarea"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="5"
                placeholder="Event description"
              />
            </div>

            <div className="form-group full-width">
              <label>Event Image</label>
              <div className="image-upload-section">
                {formData.image_url && (
                  <div className="image-preview">
                    <img src={formData.image_url} alt="Event" />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={isUploadingImage}
                />
                {isUploadingImage && <span className="uploading">Uploading...</span>}
                <input
                  className="dashboard-input"
                  type="text"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleInputChange}
                  placeholder="Or paste image URL"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Website URL</label>
              <input
                className="dashboard-input"
                type="url"
                name="website_url"
                value={formData.website_url}
                onChange={handleInputChange}
                placeholder="https://example.com"
              />
            </div>

            <div className="form-group">
              <label>Ticket URL</label>
              <input
                className="dashboard-input"
                type="url"
                name="ticket_url"
                value={formData.ticket_url}
                onChange={handleInputChange}
                placeholder="https://tickets.example.com"
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="is_featured"
                  checked={formData.is_featured === 1}
                  onChange={handleInputChange}
                />
                Featured Event
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active === 1}
                  onChange={handleInputChange}
                />
                Active
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button className="btn-primary" onClick={handleSave}>
              {selectedEvent ? 'Update Event' : 'Create Event'}
            </button>
            <button className="btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
